import { CloseButton, Group, type ComboboxItem } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { IconPencil, IconPlus, IconSearch, IconSquareArrowRight, IconTrash } from '@tabler/icons-react';
import { useCallback, useMemo, type CSSProperties, type ReactElement } from 'react';
import { MyTooltip } from '../../../../../../react-commons/components/MyTooltip';
import { ValidationEndAdornment } from '../../../../../../react-commons/components/ValidationEndAdornment';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import { Wrap } from '../../../../../../react-commons/components/control-flow/Wrap';
import { DisableableIconButtonWithTooltip } from '../../../../../../react-commons/components/inputs/buttons/DisableableIconButtonWithTooltip';
import type { ValueOrNullableValue } from '../../../../../../react-commons/components/inputs/controlled/types';
import { LabelNode } from '../../../../../../react-commons/components/inputs/getLabelNode';
import { getColorMantine } from '../../../../../../react-commons/components/inputs/text-fields/types';
import type { ExtractAllEnumKeys, ExtractStringKeys } from '../../../../../../react-commons/types/extractKeysOfType';
import { ControlledInputs } from '../../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { isFunction, sortBy } from '../../../../../../react-commons/utils/utilFunctions';
import type { PropertyDetailsResource } from '../../../../resource/resources/propertyDetailsResource';
import { TOOLTIPS } from '../../../../resource/tooltipTexts';
import { getPropertyLabelInfo, groupByFormatted } from '../../../util/utilFunctions';
import { getLabelFromPropertyDetails } from '../../../util/getLabelFromPropertyDetails';
import { PropertyValidation } from '../../../../resource/services/outlineTypes';

export type Controlled_AutocompleteResourceTextFieldProps<
    R extends { id: string | null; objectId?: string | null } & Record<string, unknown>,
    TTypeKey extends keyof R & ExtractAllEnumKeys<R>,
    TNullable extends boolean,
> = {
    idSelector: keyof R & ('id' | 'objectId');
    value: ValueOrNullableValue<TNullable, string>;
    resources: R | Array<R>;
    propertyDetails: PropertyDetailsResource | Array<PropertyDetailsResource> | undefined;
    labelAccessor: (keyof R & ExtractStringKeys<R, false>) | ((resource: R) => string);
    label?: string;
    propertyValidation?: PropertyValidation | null | undefined;
    onGoToClick?: (resourceId: string | undefined, openInNewTab: boolean) => void;
    typeKey?: TTypeKey;
    formatType?: (group: R[TTypeKey]) => string;
    getToolTip?: (resource: R | undefined) => ReactElement | undefined;
    onChange?: (value: ValueOrNullableValue<TNullable, string>) => void;
    style?: CSSProperties;
    disabled?: boolean;
    readOnly?: boolean;
    onEdit?: () => void;
    onAdd?: () => void;
    addTooltip?: string;
    onDelete?: () => void;
    onSearch?: () => void;
    customPlaceHolder?: string;
    hideLabel?: boolean;
    rightSectionWidthOverride?: number;
    error?: string;
    nullable?: TNullable; // TODO should not be optional => remove SetRequired from Form_Autocomplete...
    filterResources?: (resources: Array<R>, selectedId: string | null) => Array<R>;
    rightSectionText?: (resource: R | undefined) => ReactElement | undefined;
    getOptionsDisabled?: (resource: R) => boolean;
    disableAdd?: boolean;
};

export const Controlled_AutocompleteResourceTextField = <
    R extends { id: string | null; objectId?: string | null } & Record<string, unknown>,
    TTypeKey extends keyof R & ExtractAllEnumKeys<R>,
    TNullable extends boolean = true,
>({
      value,
      resources: resourceOrResources,
      propertyValidation,
      propertyDetails,
      label,
      onGoToClick,
      labelAccessor,
      typeKey,
      formatType,
      getToolTip,
      style,
      disabled = false,
      onEdit,
      idSelector,
      customPlaceHolder,
      hideLabel = false,
      onChange,
      onAdd,
      addTooltip,
      onDelete,
      onSearch,
      readOnly: _readOnly,
      rightSectionWidthOverride,
      error,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      nullable = true as TNullable,
      filterResources,
      rightSectionText,
      getOptionsDisabled,
      disableAdd = false,
  }: Controlled_AutocompleteResourceTextFieldProps<R, TTypeKey, TNullable>) => {
    const readOnly = (() => {
        if (_readOnly !== undefined) {
            return _readOnly;
        }
        if (!propertyDetails) {
            return false;
        }
        return !(Array.isArray(propertyDetails) ? propertyDetails.at(0)?.updatable : propertyDetails.updatable);
    })();

    const getOptionLabel = useCallback(
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        (r: R) => (isFunction(labelAccessor) ? labelAccessor(r) : (r[labelAccessor] as string)),
        [labelAccessor]
    );

    const severity = propertyValidation?.severity;
    const helperText = propertyValidation?.identifier;
    const { ref, width: rightSectionWidth } = useElementSize();

    const resources = useMemo(
        () => (Array.isArray(resourceOrResources) ? resourceOrResources : [resourceOrResources]),
        [resourceOrResources]
    );

    const resource = useMemo(() => {
        return resources.find((r) => {
            if (idSelector === 'objectId' && 'objectId' in r) {
                return r.objectId === value;
            } else {
                return r.id === value;
            }
        });
    }, [resources, value, idSelector]);

    const optionsArray = useMemo(() => {
        const array = (() => {
            if (!filterResources) {
                return resources;
            }
            const filteredArray = filterResources(resources, resource?.id ?? null);
            if (resource && !filteredArray.includes(resource)) {
                return filteredArray.concat(resource);
            }
            return filteredArray;
        })().toSorted(sortBy(getOptionLabel));

        if (!getOptionsDisabled) {
            return array;
        }
        return array.toSorted((a, b) => {
            const aDisabled = getOptionsDisabled(a);
            const bDisabled = getOptionsDisabled(b);
            return (
                aDisabled === bDisabled ? 0
                    : aDisabled ? 1
                        : -1
            );
        });
    }, [resources, resource, filterResources, getOptionLabel, getOptionsDisabled]);

    const data = useMemo(() => {
        const getItem = (resource: R): ComboboxItem & { description: unknown } => {
            const value = (idSelector === 'objectId' && 'objectId' in resource ? resource.objectId : resource.id) ?? '';

            const description = 'description' in resource && resource.description ? resource.description : undefined;

            return {
                value,
                label: getOptionLabel(resource),
                disabled: getOptionsDisabled?.(resource),
                description, // TODO does this do anything?
            };
        };
        const groupBy =
            typeKey &&
            (formatType ?
                (r: R) => formatType(r[typeKey])
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                :   groupByFormatted((r: R) => r[typeKey] as string));
        if (!groupBy) {
            return optionsArray.map(getItem);
        }
        return Object.entries(Object.groupBy(optionsArray, groupBy)).mapNotNull(
            ([group, values]) => values && { group, items: values.map(getItem) }
        );
    }, [optionsArray, typeKey, idSelector, getOptionLabel, formatType, getOptionsDisabled]);

    const outerRightSectionWidth = 30 * [onAdd, onSearch, onDelete].filter(Boolean).length;

    const tooltip = getToolTip?.(resource);

    const textOfRightSection = rightSectionText?.(resource);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const nullValue = null as ValueOrNullableValue<TNullable, string>;

    return (
        <Group gap={0} w={'100%'}>
            <Wrap With={MyTooltip} when={tooltip} props={{ label: tooltip }}>
                <ControlledInputs.Reference
                    error={error}
                    nullable={nullable}
                    value={value}
                    readOnly={readOnly}
                    onChange={onChange}
                    placeholder={!resource && value ? value : (customPlaceHolder ?? 'not defined')}
                    label={
                        !hideLabel && (
                            <LabelNode
                                labelInfo={getPropertyLabelInfo(propertyDetails)}
                                label={getLabelFromPropertyDetails(propertyDetails, label)}
                            />
                        )
                    }
                    data={data}
                    disabled={disabled}
                    size={'sm'}
                    style={{
                        ...style,
                        width: style?.width ?? '100%',
                        pointerEvents: disabled ? 'none' : undefined,
                    }}
                    styles={{
                        input: {
                            borderColor: getColorMantine(severity),
                            backgroundColor: readOnly ? 'var(--mantine-color-read-only-input)' : undefined,
                        },
                    }}
                    rightSectionWidth={rightSectionWidthOverride ?? rightSectionWidth}
                    rightSection={
                        <Group
                            ref={ref}
                            gap={4}
                            align={'center'}
                            wrap={'nowrap'}
                            pr={4}
                            justify={textOfRightSection ? 'right' : undefined}
                            mx={textOfRightSection ? 0 : undefined}
                        >
                            {textOfRightSection}
                            <Show when={resource && !readOnly && nullable}>
                                <CloseButton size="md" variant="transparent" onClick={() => onChange?.(nullValue)} />
                            </Show>
                            <Show when={onEdit}>
                                <DisableableIconButtonWithTooltip
                                    disabled={disabled || !resource}
                                    tooltipText={TOOLTIPS.EDIT_REFERENCE}
                                    icon={<IconPencil />}
                                    onClick={onEdit}
                                />
                            </Show>
                            <Show when={onGoToClick}>
                                {(onGoTo) => (
                                    <DisableableIconButtonWithTooltip
                                        disabled={disabled || !resource}
                                        tooltipText={TOOLTIPS.GO_TO_REFERENCE}
                                        icon={<IconSquareArrowRight />}
                                        onClick={() => onGoTo(resource?.id ?? undefined, false)}
                                        onAuxClick={(event) => {
                                            if (event.button === 1) {
                                                onGoTo(resource?.id ?? undefined, true);
                                            }
                                        }}
                                    />
                                )}
                            </Show>
                            <ValidationEndAdornment severity={severity} helperText={helperText ? [helperText] : undefined} />
                        </Group>
                    }
                />
            </Wrap>
            <Show when={!readOnly}>
                <Group
                    miw={outerRightSectionWidth}
                    gap={6}
                    style={{
                        flexGrow: 1,
                        display: 'inline-flex',
                        paddingTop: !hideLabel ? 26 : 0,
                        paddingLeft: 4,
                        alignItems: 'center',
                    }}
                >
                    <Show when={onSearch}>
                        <DisableableIconButtonWithTooltip
                            disabled={disabled}
                            tooltipText={TOOLTIPS.SEARCH_FOR_REFERENCE}
                            icon={<IconSearch />}
                            onClick={onSearch}
                        />
                    </Show>
                    <Show when={onAdd}>
                        <DisableableIconButtonWithTooltip
                            disabled={disabled || disableAdd}
                            tooltipText={addTooltip ?? TOOLTIPS.CREATE_NEW_REFERENCE}
                            icon={<IconPlus />}
                            onClick={onAdd}
                        />
                    </Show>
                    <Show when={onDelete}>
                        <DisableableIconButtonWithTooltip
                            disabled={disabled || !resource}
                            tooltipText={TOOLTIPS.DELETE}
                            icon={<IconTrash />}
                            onClick={onDelete}
                        />
                    </Show>
                </Group>
            </Show>
        </Group>
    );
};


